import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, FileDown } from "lucide-react";
import { VESSEL_CATALOGUE } from "@/data/portalMockData";
import SEOHead from "@/components/SEOHead";
import logo from "@/assets/velocity-logo-header.png";

const FleetBrochure = () => {
  const { vesselId } = useParams();
  const single = vesselId ? VESSEL_CATALOGUE.find((v) => v.id === vesselId) : null;
  const vessels = single ? [single] : VESSEL_CATALOGUE;

  return (
    <div className="min-h-screen bg-muted/40 py-8 print:bg-white print:py-0">
      <SEOHead />

      {/* Toolbar (hidden on print) */}
      <div className="max-w-[860px] mx-auto mb-6 flex items-center justify-between px-4 print:hidden">
        <Link
          to={single ? "/fleet-brochure" : "/fleet"}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> {single ? "All brochures" : "Back to Fleet"}
        </Link>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
        >
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      {/* Individual vessel brochures index (hidden on print) */}
      {!single && (
        <div className="max-w-[860px] mx-auto mb-8 px-4 print:hidden">
          <div className="rounded-xl border border-border bg-background p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[3px] text-navy mb-3">
              Individual vessel brochures
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {VESSEL_CATALOGUE.map((v) => (
                <Link
                  key={v.id}
                  to={`/fleet-brochure/${v.id}`}
                  className="inline-flex items-center gap-2 rounded-md border border-teal/40 px-3 py-2 text-xs font-semibold text-teal hover:bg-teal/10"
                >
                  <FileDown className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{v.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="brochure mx-auto max-w-[860px] space-y-8 px-4 print:max-w-none print:space-y-0 print:px-0">

        {/* ── Cover ── */}
        {!single && (
        <section className="brochure-page bg-navy-deep text-primary-foreground rounded-xl overflow-hidden shadow-lg print:rounded-none print:shadow-none">
          <div className="p-12 md:p-16">
            <img src={logo} alt="Velocity Offshore" className="h-28 mb-8" />
            <p className="text-[11px] font-semibold uppercase tracking-[4px] text-teal">Fleet Catalogue</p>
            <h1 className="font-display text-[clamp(2.4rem,6vw,4rem)] leading-[1.05] tracking-tight mt-3">
              Meet Our Fleet
            </h1>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-primary-foreground/80">
              Purpose-built offshore assets engineered for safe, fast and cost-efficient deepwater logistics.
              Every vessel in this catalogue is available for charter or newbuild programmes.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              {VESSEL_CATALOGUE.map((v, i) => (
                <div key={v.id} className="border-t border-primary-foreground/20 pt-3">
                  <p className="text-[10px] tracking-[2px] uppercase text-teal">{String(i + 1).padStart(2, "0")}</p>
                  <p className="text-xs text-primary-foreground/85 leading-snug">{v.name}</p>
                </div>
              ))}
            </div>
            <p className="mt-12 text-[10px] uppercase tracking-[3px] text-primary-foreground/50">
              Velocity Offshore Holdings LLC · velocityoffshore.com
            </p>
          </div>
        </section>
        )}

        {/* ── One page per vessel ── */}
        {vessels.map((v) => (
          <section

            key={v.id}
            className="brochure-page bg-background rounded-xl overflow-hidden shadow-lg print:rounded-none print:shadow-none"
          >
            <img src={v.image} alt={v.name} className="w-full h-[300px] object-cover print:h-[170px]" />
            <div className="p-8 md:p-12 print:p-6">

              <div className="flex items-baseline justify-between gap-4 border-b border-border pb-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[3px] text-teal">
                    {String(VESSEL_CATALOGUE.indexOf(v) + 1).padStart(2, "0")} · {v.type}
                  </p>
                  <h2 className="font-display text-2xl md:text-3xl text-navy mt-1">{v.name}</h2>
                </div>
                <img src={logo} alt="" className="h-10 opacity-80 hidden md:block print:block" />
              </div>

              <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{v.description}</p>

              <p className="mt-8 mb-3 text-[10px] font-semibold uppercase tracking-[3px] text-navy">
                Principal Particulars
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border">
                {v.specs.map((s) => (
                  <div key={s.label} className="bg-background p-3">
                    <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
                    <p className="text-xs font-medium text-foreground leading-snug">{s.value}</p>
                  </div>
                ))}
              </div>

              <p className="mt-8 pt-4 border-t border-border text-[10px] uppercase tracking-[2px] text-muted-foreground">
                Velocity Offshore Holdings LLC · Specifications indicative and subject to final design
              </p>
            </div>
          </section>
        ))}
      </div>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 8mm; }
          .brochure-page { break-after: page; page-break-after: always; break-inside: avoid; }
          .brochure-page:last-child { break-after: auto; page-break-after: auto; }
          .brochure-page h2 { font-size: 20px; }
          .brochure-page p { font-size: 10.5px; line-height: 1.45; }
          .brochure-page .text-\\[9px\\] { font-size: 7.5px; }
          .brochure-page, .brochure-page * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

    </div>
  );
};

export default FleetBrochure;
